import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import supabase from '../../../lib/supabaseClient';
import { loadLearningPaths as loadLearningPathsFromStorage } from '../../../utils/learningPathsStorage';

const LearningPaths = () => {
  const { user, userProfile } = useAuth();
  const [learningPaths, setLearningPaths] = useState([]);
  const [userProgress, setUserProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [showLearningModal, setShowLearningModal] = useState(false);
  const [currentPath, setCurrentPath] = useState(null);
  const [currentModule, setCurrentModule] = useState(1);
  const [moduleContent, setModuleContent] = useState(null);

  useEffect(() => {
    loadLearningPaths();
    if (user) {
      loadUserProgress();
    }

    // Listen for localStorage changes (when admin updates data)
    const handleStorageChange = (e) => {
      if (e.key === 'bootminds_learning_paths') {
        console.log('LearningPaths: Detected localStorage change, reloading data');
        loadLearningPaths();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom events (for same-tab updates)
    const handleCustomStorageChange = () => {
      console.log('LearningPaths: Detected custom storage change, reloading data');
      loadLearningPaths();
    };

    window.addEventListener('learningPathsStorageChanged', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('learningPathsStorageChanged', handleCustomStorageChange);
    };
  }, [user]);

  const loadLearningPaths = async () => {
    try {
      console.log('=== LearningPaths: Loading learning paths from localStorage ===');

      const data = await loadLearningPathsFromStorage();
      console.log('=== LearningPaths: Raw data received from storage:', data);
      console.log('=== LearningPaths: Raw data as JSON:', JSON.stringify(data, null, 2));


      if (!data || data.length === 0) {
        console.log('No learning paths found, using fallback');
        setLearningPaths([]);
      } else {
        // Filter to only published paths and add steps for display
        console.log('=== LearningPaths: All paths before filtering:', data.map(p => ({ title: p.title, status: p.status })));

        const publishedPaths = data
          .filter(path => {
            console.log(`Filtering path "${path.title}": status="${path.status}", isPublished=${path.status === 'published'}`);
            return path.status === 'published';
          })
          .map(path => {
            const steps = getStepsForPath(path.title);
            console.log(`Processing path "${path.title}": ${steps.length} steps`);
            return {
              ...path,
              steps: steps,
              rating: path.rating || 4.5,
              enrolled_count: path.enrolled_count || 0
            };
          });

        console.log(`Loaded ${publishedPaths.length} published learning paths:`, publishedPaths.map(p => ({ title: p.title, stepsCount: p.steps.length })));
        console.log('=== LearningPaths: Setting learning paths state');
        setLearningPaths(publishedPaths);
      }
    } catch (error) {
      console.error('Failed to load learning paths:', error);
      console.error('Error details:', error);
      setLearningPaths([]);
    } finally {
      console.log('=== LearningPaths: Setting loading to false');
      setLoading(false);
    }
  };

  const loadUserProgress = async () => {
    try {
      const { data, error } = await supabase
        .from('user_learning_progress')
        .select(`
          *,
          learning_paths (
            title,
            modules_count
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;
      
      const progressMap = {};
      data?.forEach(progress => {
        progressMap[progress.learning_path_id] = progress;
      });
      setUserProgress(progressMap);
    } catch (error) {
      console.error('Error loading user progress:', error);
    }
  };


  const getPathColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'beginner':
        return 'from-success to-success/80';
      case 'intermediate':
        return 'from-warning to-warning/80';
      case 'advanced':
        return 'from-error to-error/80';
      case 'expert':
        return 'from-accent to-accent/80';
      default:
        return 'from-primary to-primary/80';
    }
  };

  const getPathIcon = (title) => {
    if (title.includes('GCC Setup')) return 'Building';
    if (title.includes('Talent')) return 'Users';
    if (title.includes('Compliance')) return 'Shield';
    if (title.includes('Operational')) return 'Zap';
    if (title.includes('Technology')) return 'Cpu';
    return 'BookOpen';
  };

  const getStepsForPath = (title) => {
    // Return appropriate steps based on the path title
    if (title.includes('GCC Setup')) {
      return [
        "Strategic Planning & Business Case",
        "Location Selection & Market Analysis",
        "Legal Structure & Compliance",
        "Talent Acquisition Strategy",
        "Operational Setup & Infrastructure",
        "Performance Management & Optimization"
      ];
    } else if (title.includes('Talent')) {
      return [
        "Talent Market Intelligence",
        "Recruitment Strategy Design",
        "Interview & Assessment Frameworks",
        "Onboarding Excellence",
        "Performance Management",
        "Retention & Career Development"
      ];
    } else if (title.includes('Compliance')) {
      return [
        "Regulatory Landscape Overview",
        "Corporate Structure & Taxation",
        "Employment Law Compliance",
        "Data Protection & Security",
        "Audit & Reporting Requirements",
        "Risk Mitigation Strategies"
      ];
    } else if (title.includes('Operational')) {
      return [
        "Process Design & Optimization",
        "Quality Management Systems",
        "Technology Integration",
        "Metrics & KPI Framework",
        "Continuous Improvement Culture",
        "Scaling & Expansion Strategies"
      ];
    } else if (title.includes('Technology')) {
      return [
        "Technology Stack Assessment",
        "Infrastructure Planning",
        "Security & Compliance",
        "Integration Strategies",
        "Performance Optimization",
        "Innovation & Future Trends"
      ];
    } else {
      return [
        "Module 1: Introduction",
        "Module 2: Core Concepts",
        "Module 3: Advanced Topics",
        "Module 4: Practical Application",
        "Module 5: Assessment",
        "Module 6: Certification"
      ];
    }
  };

  const getModuleContent = (pathTitle, moduleNumber) => {
    const moduleContents = {
      "Technology & Innovation Excellence": {
        1: {
          title: "Technology Stack Assessment",
          content: `
            <h2>Module 1: Technology Stack Assessment</h2>
            <p>In this module, you'll learn how to evaluate and select the optimal technology stack for your GCC operations.</p>
            
            <h3>Key Learning Objectives:</h3>
            <ul>
              <li>Understand current technology landscape</li>
              <li>Evaluate technology stack requirements</li>
              <li>Assess scalability and performance needs</li>
              <li>Consider security and compliance factors</li>
            </ul>
            
            <h3>Video Content:</h3>
            <div class="bg-muted p-4 rounded-lg text-center">
              <Icon name="Play" size={48} className="mx-auto mb-2 text-primary" />
              <p>Technology Stack Assessment Overview (15:30)</p>
            </div>
            
            <h3>Reading Materials:</h3>
            <ul>
              <li>Technology Stack Evaluation Framework</li>
              <li>Case Study: Successful GCC Technology Implementation</li>
              <li>Best Practices for Technology Selection</li>
            </ul>
            
            <h3>Interactive Exercise:</h3>
            <p>Complete the Technology Stack Assessment Worksheet to evaluate your organization's current and future technology needs.</p>
          `,
          duration: "45 minutes",
          resources: ["PDF Guide", "Assessment Tool", "Video Tutorial"]
        },
        2: {
          title: "Infrastructure Planning",
          content: `
            <h2>Module 2: Infrastructure Planning</h2>
            <p>Learn how to design and plan robust infrastructure for your GCC operations.</p>
            
            <h3>Key Learning Objectives:</h3>
            <ul>
              <li>Design scalable infrastructure architecture</li>
              <li>Plan for disaster recovery and business continuity</li>
              <li>Optimize for cost and performance</li>
              <li>Ensure security and compliance</li>
            </ul>
            
            <h3>Video Content:</h3>
            <div class="bg-muted p-4 rounded-lg text-center">
              <Icon name="Play" size={48} className="mx-auto mb-2 text-primary" />
              <p>Infrastructure Planning Deep Dive (22:15)</p>
            </div>
            
            <h3>Reading Materials:</h3>
            <ul>
              <li>Infrastructure Planning Guide</li>
              <li>Cloud vs On-Premise Decision Framework</li>
              <li>Cost Optimization Strategies</li>
            </ul>
          `,
          duration: "60 minutes",
          resources: ["Planning Template", "Cost Calculator", "Architecture Diagrams"]
        }
      },
      "GCC Setup Mastery": {
        1: {
          title: "Strategic Planning & Business Case",
          content: `
            <h2>Module 1: Strategic Planning & Business Case</h2>
            <p>Develop a comprehensive strategic plan and compelling business case for your GCC setup.</p>
            
            <h3>Key Learning Objectives:</h3>
            <ul>
              <li>Define GCC strategy and objectives</li>
              <li>Create compelling business case</li>
              <li>Identify key success factors</li>
              <li>Plan stakeholder engagement</li>
            </ul>
            
            <h3>Video Content:</h3>
            <div class="bg-muted p-4 rounded-lg text-center">
              <Icon name="Play" size={48} className="mx-auto mb-2 text-primary" />
              <p>Strategic Planning Fundamentals (18:45)</p>
            </div>
            
            <h3>Reading Materials:</h3>
            <ul>
              <li>Strategic Planning Framework</li>
              <li>Business Case Template</li>
              <li>ROI Calculation Guide</li>
            </ul>
          `,
          duration: "50 minutes",
          resources: ["Strategy Template", "ROI Calculator", "Stakeholder Map"]
        }
      }
    };

    return moduleContents[pathTitle]?.[moduleNumber] || {
      title: `Module ${moduleNumber}`,
      content: `
        <h2>Module ${moduleNumber}</h2>
        <p>This module contains comprehensive learning materials and interactive content.</p>
        <p>Content will be available as you progress through the learning path.</p>
      `,
      duration: "45 minutes",
      resources: ["Module Content", "Assessment", "Resources"]
    };
  };

  const handleStartLearning = async (pathId) => {
    try {
      if (!user) {
        alert('Please sign in to start learning');
        return;
      }

      // Check if user is already enrolled
      const existingProgress = userProgress[pathId];
      
      if (!existingProgress) {
        // Enroll user in the learning path
        const { error } = await supabase
          .from('user_learning_progress')
          .insert([{
            user_id: user.id,
            learning_path_id: pathId,
            status: 'in_progress',
            progress_percentage: 0,
            current_module: 1
          }]);

        if (error) {
          console.error('Error enrolling in learning path:', error);
          alert('Error enrolling in learning path: ' + error.message);
          return;
        }
        
        // Reload progress from database
        await loadUserProgress();
      }
      
      // Open learning interface
      const path = learningPaths.find(p => p.id === pathId);
      setCurrentPath(path);
      setCurrentModule(existingProgress?.current_module || 1);
      setModuleContent(getModuleContent(path.title, existingProgress?.current_module || 1));
      setShowLearningModal(true);
      
    } catch (error) {
      console.error('Error starting learning:', error);
      alert('Error starting learning: ' + error.message);
    }
  };

  const handleModuleComplete = async () => {
    try {
      const progress = userProgress[currentPath.id];
      const currentProgress = progress?.progress_percentage || 0;
      const totalModules = currentPath.modules_count || 6;
      const newProgress = Math.min(currentProgress + (100 / totalModules), 100);
      const newModule = Math.min(currentModule + 1, totalModules);
      const newStatus = newProgress >= 100 ? 'completed' : 'in_progress';
      
      // Update progress in database
      const { error } = await supabase
        .from('user_learning_progress')
        .update({
          progress_percentage: newProgress,
          current_module: newModule,
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .eq('learning_path_id', currentPath.id);

      if (error) {
        console.error('Error updating progress:', error);
        alert('Error updating progress: ' + error.message);
        return;
      }
      
      // Update local state
      setUserProgress(prev => ({
        ...prev,
        [currentPath.id]: {
          ...prev[currentPath.id],
          progress_percentage: newProgress,
          current_module: newModule,
          status: newStatus
        }
      }));
      
      // Move to next module or complete
      if (newProgress >= 100) {
        alert('🎉 Congratulations! You have completed this learning path!');
        setShowLearningModal(false);
      } else {
        setCurrentModule(newModule);
        setModuleContent(getModuleContent(currentPath.title, newModule));
        alert(`✅ Module completed! Progress: ${Math.round(newProgress)}%`);
      }
    } catch (error) {
      console.error('Error completing module:', error);
      alert('Error completing module: ' + error.message);
    }
  };

  const handleNextModule = () => {
    const nextModule = currentModule + 1;
    if (nextModule <= (currentPath?.modules_count || 6)) {
      setCurrentModule(nextModule);
      setModuleContent(getModuleContent(currentPath.title, nextModule));
    }
  };

  const handlePrevModule = () => {
    const prevModule = currentModule - 1;
    if (prevModule >= 1) {
      setCurrentModule(prevModule);
      setModuleContent(getModuleContent(currentPath.title, prevModule));
    }
  };

  if (loading) {
    return (
      <div className="mb-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-text-secondary">Loading learning paths...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-12">
      <div className="text-center mb-8">
        <h2 className="font-headline font-bold text-3xl mb-4 text-text-primary">
          Structured Learning Paths
        </h2>
        <p className="text-text-secondary max-w-2xl mx-auto mb-4">
          Master GCC excellence through our comprehensive, step-by-step learning journeys designed by industry experts.
        </p>
        <div className="flex gap-4 justify-center mb-4">
          <button
            onClick={() => {
              localStorage.removeItem('bootminds_learning_paths');
              loadLearningPaths();
            }}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm"
          >
            Force Refresh Data
          </button>
          <button
            onClick={() => {
              const data = localStorage.getItem('bootminds_learning_paths');
              console.log('=== Current localStorage data ===');
              console.log('Raw data:', data);
              if (data) {
                const parsed = JSON.parse(data);
                console.log('Parsed data:', parsed);
                console.log('Number of paths:', parsed.length);
                parsed.forEach((path, index) => {
                  console.log(`Path ${index + 1}:`, { title: path.title, status: path.status });
                });
              } else {
                console.log('No data in localStorage');
              }
            }}
            className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-colors text-sm"
          >
            Check localStorage
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {console.log('=== LearningPaths: Rendering paths:', learningPaths.map(p => p.title))}
        {learningPaths.map((path) => {
          console.log(`=== LearningPaths: Rendering path: "${path.title}", steps:`, path.steps?.length || 0);
          const progress = userProgress[path.id];
          const progressPercentage = progress ? progress.progress_percentage || 0 : 0;
          const isEnrolled = progress && progress.status !== 'not_started';
          
          return (
            <div
              key={path.id}
              className="group card-interactive rounded-xl overflow-hidden"
            >
              {/* Header with Gradient */}
              <div className={`bg-gradient-to-r ${getPathColor(path.level)} p-6 text-primary-foreground`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-primary-foreground/20 rounded-lg flex items-center justify-center mr-4">
                      <Icon name={getPathIcon(path.title)} size={24} />
                    </div>
                    <div>
                      <h3 className="font-headline font-semibold text-xl mb-1">
                        {path.title}
                      </h3>
                      <p className="opacity-90 text-sm">
                        {path.description}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm">
                    <div className="flex items-center">
                      <Icon name="BarChart" size={16} className="mr-1" />
                      {path.level}
                    </div>
                    <div className="flex items-center">
                      <Icon name="Clock" size={16} className="mr-1" />
                      {path.duration}
                    </div>
                    <div className="flex items-center">
                      <Icon name="BookOpen" size={16} className="mr-1" />
                      {path.modules_count} modules
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Stats */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4 text-sm text-text-secondary">
                    <div className="flex items-center">
                      <Icon name="Users" size={16} className="mr-1" />
                      {path.enrolled_count?.toLocaleString() || '0'} enrolled
                    </div>
                    <div className="flex items-center">
                      <Icon name="Star" size={16} className="mr-1 text-yellow-400" />
                      {path.rating || '4.5'}
                    </div>
                  </div>
                </div>

                {/* Learning Steps */}
                {path.steps && (
                  <div className="mb-6">
                    <h4 className="font-medium text-text-primary mb-3">Learning Journey</h4>
                    <div className="space-y-2">
                      {path.steps.map((step, index) => (
                        <div key={index} className="flex items-center">
                          <div className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-medium mr-3">
                            {index + 1}
                          </div>
                          <span className="text-sm text-text-secondary">{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Progress Indicator */}
                {user && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-text-secondary">Your Progress</span>
                      <span className="text-primary font-medium">{progressPercentage}% Complete</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center space-x-3">
                  {user && userProfile?.approval_status === 'approved' ? (
                    <button
                      onClick={() => handleStartLearning(path.id)}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-300 font-medium"
                    >
                      <Icon name={isEnrolled ? "Play" : "Play"} size={16} className="mr-2" />
                      {isEnrolled ? "Continue Learning" : "Start Learning"}
                    </button>
                  ) : (
                    <button
                      onClick={() => alert('Please sign in and get approved to access learning paths')}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-muted text-text-secondary rounded-lg cursor-not-allowed"
                      disabled
                    >
                      <Icon name="Lock" size={16} className="mr-2" />
                      Sign In Required
                    </button>
                  )}
                  <button className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors duration-300">
                    <Icon name="Bookmark" size={16} />
                  </button>
                  <button className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors duration-300">
                    <Icon name="Share2" size={16} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Learning Modal */}
      {showLearningModal && currentPath && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-xl font-semibold text-text-primary">
                  {currentPath.title}
                </h2>
                <p className="text-sm text-text-secondary">
                  Module {currentModule} of {currentPath.modules_count} • {moduleContent?.duration}
                </p>
              </div>
              <button
                onClick={() => setShowLearningModal(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div 
                className="prose prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: moduleContent?.content || '' }}
              />
              
              {/* Module Navigation */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
                <button
                  onClick={handlePrevModule}
                  disabled={currentModule <= 1}
                  className="flex items-center space-x-2 px-4 py-2 bg-muted text-text-secondary rounded-lg hover:bg-muted/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronLeft" size={16} />
                  <span>Previous Module</span>
                </button>
                
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleModuleComplete}
                    className="flex items-center space-x-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <Icon name="Check" size={16} />
                    <span>Complete Module</span>
                  </button>
                </div>
                
                <button
                  onClick={handleNextModule}
                  disabled={currentModule >= (currentPath.modules_count || 6)}
                  className="flex items-center space-x-2 px-4 py-2 bg-muted text-text-secondary rounded-lg hover:bg-muted/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>Next Module</span>
                  <Icon name="ChevronRight" size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningPaths;